import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { CircularProgress, Box, Button, ListItemButton, TextField, IconButton, Typography, Paper, Grid2, Badge, AppBar, Toolbar, List, ListItemText, ListItemAvatar, Dialog, Slide } from "@mui/material";
//import { IoSend, IoCheckmarkDone, IoCheckmark } from "react-icons/io5";
import Send from '@mui/icons-material/Send';
import Check from '@mui/icons-material/Check';

import dayjs from "dayjs"
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';


import PhoneIcon from '@mui/icons-material/Phone';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import BlockIcon from '@mui/icons-material/Block';

import { MuiTelInput } from 'mui-tel-input';
import { AXIOS_API_INSTANCE } from "../../Constants/index.js";
import { send_authorized_request } from "../../Constants/cognito"
import { useSnackbar } from "notistack"
import { useTrackedLocation } from "../UserState.js";
import { findUser, useUsers } from "../ReactQuery/QueryUsers";
import SmsIcon from '@mui/icons-material/Sms';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { findLocation, useLocations } from "../ReactQuery/QueryLocations"
import { formatCustomerName, formatPhoneNumber } from "../../Utils/index.js";
import { useSmsUnread } from "../ReactQuery/QuerySmsUnread.js";
import CustomerModal from "../CustomerModal.js";

dayjs.extend(isToday);
dayjs.extend(isYesterday);


export const ChatAppStyledPaper = styled(Paper)(({ theme, fullHeight = false }) => ({
    height: fullHeight && "100vh",
    borderRadius: fullHeight && 0,
    overflow: "hidden",
    display: "flex",
    width: "100%",
    flexDirection: "column",
    backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f5f5f5"
}));

export const ChatAppMessageContainer = styled(Box)(({ theme, sent }) => ({
    display: "flex",
    width: "100%",
    justifyContent: sent ? "flex-end" : "flex-start",
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1)
}));

export const ChatAppMessageBubble = styled(Paper)(({ theme, sent, fullWidth, other_location }) => ({
    padding: theme.spacing(1, 2),
    maxWidth: fullWidth ? "100%" : "70%",
    borderRadius: theme.spacing(2),
    backgroundColor: sent ? (theme.palette.mode === "dark" ? theme.palette.secondary.dark : theme.palette.secondary.main) : theme.palette.background.paper,
    opacity: other_location ? 0.4 : 1,
    color: sent ? theme.palette.primary.contrastText : theme.palette.text.primary
}));

export const ChatAppContacts = ({ contacts, selectedContact, setSelectedContact }) => {
    return (
        <List>
            {contacts.map((contact) => (
                <ListItemButton
                    key={contact.phone}
                    selected={selectedContact?.phone === contact.phone}
                    onClick={() => {
                        if (contact !== selectedContact) {
                            setSelectedContact(contact)
                        }
                    }
                    }
                >
                    <ListItemAvatar>
                        <Badge
                            badgeContent={<BlockIcon />}
                            invisible={!contact.blacklist}
                        >
                            <Badge
                                color="secondary"
                                badgeContent={contact.unread}
                                invisible={!contact.unread}
                            >
                                <PhoneIcon />
                            </Badge>
                        </Badge>
                    </ListItemAvatar>
                    <ListItemText
                        // primary={<Typography sx={{ textDecoration: 'line-through' }}>{formatPhoneNumber(contact.phone)}</Typography>}
                        primary={formatPhoneNumber(contact.phone)}
                        slotProps={contact.blacklist && { primary: { sx: { textDecoration: 'line-through' } } }}
                        secondary={contact.customers.map(formatCustomerName).join(', ')}
                    />
                </ListItemButton>
            ))}
        </List>
    )
}

export const ChatAppMessages = ({ messages }) => {
    const { data: users } = useUsers();
    const { data: locations, } = useLocations();
    const [selectedLocation,] = useTrackedLocation(); // REMOVED setSelectedLocation to make linter happy
    const messagesEndRef = useRef(null);
    const prevMessagesLength = useRef(messages.length);
    const scrollToBottom = (boxRef, instant) => {
        boxRef.current?.scrollIntoView({ behavior: instant ? 'instant' : "smooth" });
    };

    const formatMessageTime = (timestamp) => {
        const time = dayjs.utc(timestamp).local();
        var formattedTime = time.format('MMM D');
        var tooltip = time.format('h:mm A');

        if (time.isToday()) {
            formattedTime = time.format('hh:mm A');
            tooltip = ""
        } else if (time.isYesterday()) {
            formattedTime = 'Yesterday';
        }
        return (
            <Tooltip title={tooltip}>
                <Typography variant="caption" sx={{ mr: 0.5 }}>
                    {formattedTime}
                </Typography>
            </Tooltip>)
    };
    useEffect(() => {
        if (messages.length > prevMessagesLength.current) {
            scrollToBottom(messagesEndRef, prevMessagesLength.current === 0);
        }
        prevMessagesLength.current = messages.length;
    }, [messages]);
    return (
        <Box>
            {messages.sort((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).map((message) => {
                const user = findUser(users, message.user_id)
                const locationName = findLocation(locations, message.location_id)
                return (
                    <ChatAppMessageContainer key={message.id} sent={message.source === "INTERNAL"}>
                        {message.status === "undelivered" && <Tooltip title="Message was Undeliverable"><SmsFailedIcon color="error" sx={{ mr: 1 }} /></Tooltip>}
                        {message.status === "queued" && <CircularProgress size={'20px'} sx={{ mr: 1 }} />}
                        <ChatAppMessageBubble sent={message.source === "INTERNAL"} other_location={selectedLocation.id !== 'all' && selectedLocation.id !== message.location_id} fullWidth={message.source === "INTERNAL"}>
                            <Typography sx={{ overflowWrap: 'anywhere' }}>{message.message}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 0.5 }}>
                                <Typography variant="caption" sx={{ mr: 0.5 }}>
                                    {message.source === "INTERNAL" && (
                                        user?.name + " | " + locationName?.name + " |"
                                    )}
                                    {message.source !== "INTERNAL" && (
                                        locationName?.name + " |"
                                    )}
                                </Typography>
                                {formatMessageTime(message.date_created)}
                                {message.source !== "INTERNAL" ? (
                                    message.read_time !== null ? <Check /> : <MarkUnreadChatAltIcon />
                                ) : (
                                    message.status === "delivered" ? <Tooltip title="Message Was Delivered"><SmsIcon /></Tooltip> :
                                        message.status === "undelivered" ? <Tooltip title="The Message was Undeliverable"><SmsFailedIcon /></Tooltip> :
                                            message.status === "queued" ? <Tooltip title="Message is Queued"><PendingActionsIcon /></Tooltip> :
                                                message.status === "sent" ? <Tooltip title="Message was sent"><SmsIcon /></Tooltip> :
                                                    ''
                                )}

                            </Box>
                        </ChatAppMessageBubble>
                    </ChatAppMessageContainer>
                )
            }
            )}
            <div ref={messagesEndRef} />
        </Box>
    )
}

export const ChatAppTypeMessage = ({ selectedContact, setMessages, setIsLoading, refetch }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { refetch: refetchTotalUnread } = useSmsUnread();
    const [selectedLocation,] = useTrackedLocation(); // REMOVED setSelectedLocation to make linter happy
    const [newMessage, setNewMessage] = useState("");
    const [sendingMessage, setSendingMessage] = React.useState(false)

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    const appendMessages = (newMessages) => {
        setMessages((prevMessages) => [...prevMessages, newMessages]);
    };

    const markMessagesRead = () => {
        setIsLoading(true)

        send_authorized_request(() => {
            refetchTotalUnread()
            // smsmessages/<phone_num>/readall
            AXIOS_API_INSTANCE.post(`smsmessages/${selectedContact.phone}/readall`, {
                location_id: selectedLocation.id // Use the selected location from the user state
            }).then((messages) => {
                console.log(messages)
                refetch();
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to Mark Messages as Read <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        })

    }

    const handleSendMessage = () => {
        if (newMessage.trim()) {
            const newMsg = {
                phone: selectedContact.phone,
                message: newMessage.trim(),
                location_id: selectedLocation.id
            };
            setSendingMessage(true)
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.put(`smsmessages`, newMsg
                ).then((messages) => {
                    setSendingMessage(false)
                    setNewMessage("");
                    console.log(messages)
                    appendMessages(messages.data.data)

                    setIsLoading(false)
                }).catch((error) => {
                    setSendingMessage(false)
                    setIsLoading(false)
                    enqueueSnackbar(`Failed to get Send Message <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
    };
    return (
        <Box sx={{ p: 2, backgroundColor: "background.paper" }}>
            <Grid2 container spacing={2} alignItems="center">
                <Grid2 size={"grow"}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        label={selectedLocation.id !== 'all' && `From: ${selectedLocation.name}`}
                        slotProps={{ inputLabel: { shrink: true } }}
                        placeholder="Type a message..."
                        variant="outlined"
                        disabled={selectedLocation.id === 'all'}
                    />
                </Grid2>

                <Grid2 size={"auto"} sx={{ justifyContent: "center", alignItems: "center", }}>
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sendingMessage}
                    >
                        <Send />
                    </IconButton>
                    <IconButton
                        color="primary"
                        onClick={markMessagesRead}
                        disabled={sendingMessage || selectedLocation.id === 'all'}
                    >
                        <MarkChatReadIcon />
                    </IconButton>
                </Grid2>
            </Grid2>
        </Box>
    )
}

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="right" ref={ref} {...props} />;
});

const ChatApp = ({ open, onClose }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { refetch: refetchTotalUnread } = useSmsUnread();
    const [selectedLocation,] = useTrackedLocation(); // REMOVED setSelectedLocation to make linter happy
    const [isLoading, setIsLoading] = React.useState(true)

    const [contacts, setContacts] = useState([]);
    const [messages, setMessages] = useState([]);
    const [customerModal, setCustomerModal] = React.useState({
        open: false,
        customer: {},
        creatingNewCustomer: false,
    })

    const [selectedContact, setSelectedContact] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");

    const valid_phone = phoneNumber.length === 15;

    const sortedContacts = contacts.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);

    useEffect(() => {
        if (open) {
            fetchContacts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (selectedContact && open) {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContact]);


    useEffect(() => {
        const some_sending = messages.some((m) => m.status === "queued")
        const intervalId = setInterval(() => {
            if (open) {
                //Need to not Fetch new Messages if there are no changes
                if (selectedContact) {
                    fetchMessages();
                }
                fetchContacts();
            }
        }, some_sending ? 1000 : 300000); // Adjust the interval time as needed (300000ms = 5 Minutes)

        // Cleanup the interval on component unmount or when selectedContact changes
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, open]);



    const fetchContacts = () => {
        //Only set is Loading if no Data is allready Present.
        if (!contacts) {
            setIsLoading(true)
        }

        send_authorized_request(() => {
            refetchTotalUnread()
            AXIOS_API_INSTANCE.get("smsmessages", {
                params: {
                    page_num: 0,
                    page_size: 1000, // TODO: SUPPORT PAGINATION
                    location_id: selectedLocation.id, // Use the selected location from the user state
                },
            }).then((contactsNumbers) => {
                console.log(contactsNumbers)
                setContacts(contactsNumbers.data.data)

                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to get messages <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        })
    }

    const fetchMessages = () => {
        if (messages.length === 0) {
            setIsLoading(true)
        }

        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get(`smsmessages/${selectedContact.phone}`, {

            }).then((messages) => {
                console.log(messages)
                setMessages(messages.data.data)

                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to get messages <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    // const fetchNumberofUnread = (contactPhone) => {
    //     if (!messages) {
    //         setIsLoading(true)
    //     }


    //     send_authorized_request(() =>
    //         AXIOS_API_INSTANCE.get(`smsmessages/${contactPhone}/unread`, {

    //         }).then((messages) => {
    //             console.log(messages)
    //             //setMessages(messages.data.data)

    //             setIsLoading(false)
    //         }).catch((error) => {
    //             setIsLoading(false)
    //             enqueueSnackbar(`Failed to get Unread Message Count <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
    //         })
    //     )
    // }




    const handlePhoneNumberChange = (e, extra) => {


        if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
            setPhoneNumber(e)
        }

    };

    const handleStartChat = () => {
        if (phoneNumber.trim()) {

            const newContact = {
                id: contacts.length + 1,
                phone: phoneNumber.substring(3).replaceAll(' ', ''),
                messagesUnread: false,
                customers: []
            };
            setContacts([...contacts, newContact]);
            setSelectedContact(newContact);
            setPhoneNumber("");
        }
    };

    return (
        <Dialog
            fullScreen
            open={open}
            slots={{ transition: Transition }}
            PaperProps={{ sx: { borderRadius: "0px" } }}
            slotProps={{ paper: { sx: { borderRadius: "0px" } } }}
        >
            <ChatAppStyledPaper fullHeight>
                <AppBar position="static">
                    <Toolbar>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1, mr: 1 }}>
                            Messages
                        </Typography>
                        {(selectedContact ?? { customers: [] }).customers.map((c) =>
                            <Button color="inherit" variant="outlined" key={c.id} onClick={() => {
                                setCustomerModal({
                                    customer: c,
                                    open: true,
                                    creatingNewCustomer: false,
                                })
                            }}
                                sx={{ mr: 1 }}>{formatCustomerName(c)}</Button>
                        )}
                        <Button color="inherit" onClick={() => { setSelectedContact(null) }}>New Message</Button>
                        <IconButton
                            aria-label="close"
                            onClick={() => onClose()}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                <Grid2 container spacing={2} sx={{ overflow: "hidden" }}>
                    {/* The contacts list on the left side of the screen */}
                    <Grid2 size={4} >
                        <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 48px)' }}>
                            {/*Show the load circle while we wait for the contacts */}
                            {isLoading && !selectedContact ? <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%', // Adjust the height as needed
                                }}
                            >
                                <CircularProgress />
                            </Box> :
                                <ChatAppContacts
                                    contacts={sortedContacts}
                                    selectedContact={selectedContact}
                                    setSelectedContact={(contact) => {
                                        setMessages([])
                                        setSelectedContact(contact)
                                    }}
                                />
                            }
                        </Box>
                    </Grid2>

                    <Grid2 size={8}>

                        {selectedContact ? (
                            (<Box sx={{ height: 'calc(100vh - 48px - 88px)', overflow: 'auto' }}>
                                {isLoading &&
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '100%', // Adjust the height as needed
                                        }}
                                    >
                                        <CircularProgress size="7rem" />
                                    </Box>}
                                <ChatAppMessages
                                    messages={messages}
                                />
                            </Box>)
                        ) : (
                            <Box display="flex" flexDirection="column" gap={2} sx={{ p: 2 }}>

                                <MuiTelInput fullWidth value={phoneNumber} onChange={handlePhoneNumberChange} defaultCountry="US"
                                    id="phone"
                                    label="Phone"
                                    type="tel"
                                    inputMode="numeric"
                                    variant="filled"
                                    onlyCountries={['US']}

                                    forceCallingCode
                                    disableDropdown />


                                <Button fullWidth onClick={handleStartChat}
                                    disabled={!valid_phone} variant="contained" endIcon={<Send />}>
                                    Begin
                                </Button>
                            </Box>

                        )}
                        {selectedContact &&
                            <ChatAppTypeMessage
                                selectedContact={selectedContact}
                                setMessages={setMessages}
                                setIsLoading={setIsLoading}
                                refetch={() => {
                                    fetchContacts();
                                    fetchMessages();
                                }}
                            />
                        }


                    </Grid2>
                </Grid2>
                <CustomerModal
                    {...customerModal}
                    closeModal={(e) => {
                        if (e) {
                            onClose()
                        }
                        setCustomerModal({ ...customerModal, open: false })
                    }}
                    showSMS={false}
                />
            </ChatAppStyledPaper>
        </Dialog>

    );
};

export default ChatApp;